/**
 * The map overview's pure part (ADR-109's amendment of 2026-10-13, decisions 36, 38, 39 and 40): the scale a
 * map's picture is drawn at, the fit of a picture to a window, the order a map's chunks are drawn in, the
 * chunks a repaint touches, a map's rectangle at a scale and the ring of placed neighbours around a map.
 * Plain data and whole numbers; nothing here reads a layer, a prop or a tileset, and nothing names a game.
 */

import { depthOf, neighboursOf, rectOf } from "../document/layout.ts";
import type { MapDocument, WorldDocument } from "../document/types.ts";
import type { CellRect } from "../terrain/match.ts";

/**
 * The most pixels a map's picture may have along its longer side, so a picture's canvas never exceeds 4 MiB.
 */
export const OVERVIEW_PICTURE_MOST = 1_024;

/**
 * The pixels a tile may take in a picture, largest first: the open map's, a neighbour's and the World view's.
 */
export const OVERVIEW_SCALES: readonly number[] = [4, 2, 1];

/**
 * A chunk's place on its map's grid of chunks.
 */
export interface ChunkRef {
  readonly cx: number;
  readonly cy: number;
}

/**
 * A rectangle in pixels: its top-left corner and its size.
 */
export interface PixelRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * A picture fitted to a window: the zoom, and the rectangle the picture covers in the window's pixels.
 */
export interface PictureFit extends PixelRect {
  readonly zoom: number;
}

/**
 * Where a neighbour lies around a map: a side, or the corner between two sides.
 */
export type RingPosition = "north" | "north-east" | "east" | "south-east" | "south" | "south-west" | "west" | "north-west";

/**
 * One map of a ring: its id and where it lies around the map.
 */
export interface RingNeighbour {
  readonly map: string;
  readonly position: RingPosition;
}

const RING_ORDER: readonly RingPosition[] = ["north", "north-east", "east", "south-east", "south", "south-west", "west", "north-west"];

/**
 * Throws unless a value is a positive whole number.
 *
 * @param value The value.
 * @param name What the value is, for the message.
 * @throws RangeError when the value is not a positive whole number.
 */
function requirePositiveWhole(value: number, name: string): void {
  if (!Number.isInteger(value) || value < 1) {
    throw new RangeError(`${name} must be a positive whole number, got ${value}`);
  }
}

/**
 * The scale a map's picture is drawn at: the largest of 4, 2 and 1 pixel a tile at which the picture fits
 * 1,024 pixels on its longer side. A map longer than 1,024 tiles on a side gets 1, and its picture is a crop.
 *
 * @param width The map's width in tiles.
 * @param height The map's height in tiles.
 * @returns 4, 2 or 1.
 * @throws RangeError when a side is not a positive whole number of tiles.
 */
export function overviewScale(width: number, height: number): number {
  requirePositiveWhole(width, "width");
  requirePositiveWhole(height, "height");
  const longer = Math.max(width, height);
  return OVERVIEW_SCALES.find((scale) => longer * scale <= OVERVIEW_PICTURE_MOST) ?? 1;
}

/**
 * The fit of a picture to a window: the largest zoom at which the whole picture is inside the window, with
 * the picture centred.
 *
 * @param picture The picture's size in pixels.
 * @param window The window's size in pixels.
 * @returns The zoom and the rectangle the picture covers in the window.
 * @throws RangeError when a size is not a positive finite number.
 */
export function fitPicture(picture: { readonly width: number; readonly height: number }, window: { readonly width: number; readonly height: number }): PictureFit {
  for (const [value, name] of [[picture.width, "picture width"], [picture.height, "picture height"], [window.width, "window width"], [window.height, "window height"]] as const) {
    if (!Number.isFinite(value) || value <= 0) {
      throw new RangeError(`${name} must be a positive number, got ${value}`);
    }
  }
  const zoom = Math.min(window.width / picture.width, window.height / picture.height);
  const width = picture.width * zoom;
  const height = picture.height * zoom;
  return { zoom, x: (window.width - width) / 2, y: (window.height - height) / 2, width, height };
}

/**
 * Every chunk of a map, nearest first to a starting chunk, so the part a builder is looking at is drawn
 * first. Ties are broken by row and then by column, so the order is the same every time.
 *
 * @param chunksWide The map's width in chunks.
 * @param chunksHigh The map's height in chunks.
 * @param from The starting chunk, which may lie outside the map.
 * @returns A new list holding every chunk once.
 * @throws RangeError when a count is not a positive whole number or the starting chunk is not whole.
 */
export function chunkOrder(chunksWide: number, chunksHigh: number, from: ChunkRef): ChunkRef[] {
  requirePositiveWhole(chunksWide, "chunksWide");
  requirePositiveWhole(chunksHigh, "chunksHigh");
  if (!Number.isInteger(from.cx) || !Number.isInteger(from.cy)) {
    throw new RangeError(`the starting chunk must be whole, got ${from.cx}, ${from.cy}`);
  }
  const chunks: { cx: number; cy: number; distance: number }[] = [];
  for (let cy = 0; cy < chunksHigh; cy += 1) {
    for (let cx = 0; cx < chunksWide; cx += 1) {
      chunks.push({ cx, cy, distance: (cx - from.cx) ** 2 + (cy - from.cy) ** 2 });
    }
  }
  chunks.sort((a, b) => a.distance - b.distance || a.cy - b.cy || a.cx - b.cx);
  return chunks.map(({ cx, cy }) => ({ cx, cy }));
}

/**
 * The chunks of a map that a rectangle of repainted cells touches, in row order and clipped to the map.
 *
 * @param rect The cells, `x0` and `y0` inclusive and `x1` and `y1` exclusive, as the store's repaints give them.
 * @param chunkSize The chunk's side in tiles.
 * @param chunksWide The map's width in chunks.
 * @param chunksHigh The map's height in chunks.
 * @returns The chunks, none when the rectangle has no area or lies outside the map.
 * @throws RangeError when the chunk size or a count is not a positive whole number.
 */
export function chunksTouched(rect: CellRect, chunkSize: number, chunksWide: number, chunksHigh: number): ChunkRef[] {
  requirePositiveWhole(chunkSize, "chunkSize");
  requirePositiveWhole(chunksWide, "chunksWide");
  requirePositiveWhole(chunksHigh, "chunksHigh");
  if (rect.x1 <= rect.x0 || rect.y1 <= rect.y0) {
    return [];
  }
  const first = { cx: Math.max(0, Math.floor(rect.x0 / chunkSize)), cy: Math.max(0, Math.floor(rect.y0 / chunkSize)) };
  const last = { cx: Math.min(chunksWide - 1, Math.floor((rect.x1 - 1) / chunkSize)), cy: Math.min(chunksHigh - 1, Math.floor((rect.y1 - 1) / chunkSize)) };
  const touched: ChunkRef[] = [];
  for (let cy = first.cy; cy <= last.cy; cy += 1) {
    for (let cx = first.cx; cx <= last.cx; cx += 1) {
      touched.push({ cx, cy });
    }
  }
  return touched;
}

/**
 * The rectangle a placed map covers at a scale, measured from an origin, so maps drawn at their world
 * positions sit edge to edge whatever scale each picture was made at.
 *
 * @param map The map.
 * @param scale Pixels a tile.
 * @param origin The world tile the rectangle is measured from, the world's origin when omitted.
 * @returns The rectangle in pixels, or null for a map with no place.
 * @throws RangeError when the scale is not a positive whole number.
 */
export function mapRectAt(map: MapDocument, scale: number, origin: { readonly x: number; readonly y: number } = { x: 0, y: 0 }): PixelRect | null {
  requirePositiveWhole(scale, "scale");
  const rect = rectOf(map);
  if (rect === null) {
    return null;
  }
  return { x: (rect.x - origin.x) * scale, y: (rect.y - origin.y) * scale, width: rect.w * scale, height: rect.h * scale };
}

/**
 * The corner of map `a` that map `b` meets at a single point, if any.
 *
 * @param a The map.
 * @param b The other map.
 * @returns The corner, or null.
 */
function cornerOf(a: MapDocument, b: MapDocument): RingPosition | null {
  const ra = rectOf(a);
  const rb = rectOf(b);
  if (ra === null || rb === null) {
    return null;
  }
  const west = rb.x + rb.w === ra.x;
  const east = rb.x === ra.x + ra.w;
  const north = rb.y + rb.h === ra.y;
  const south = rb.y === ra.y + ra.h;
  if (north && east) {
    return "north-east";
  }
  if (south && east) {
    return "south-east";
  }
  if (south && west) {
    return "south-west";
  }
  return north && west ? "north-west" : null;
}

/**
 * Where a map starts along a side of another: its place's x for a north or south side, y for an east or west one.
 *
 * @param world The world.
 * @param mapId The map.
 * @param position The side of the other map it lies on.
 * @returns The start in world tiles, 0 for a map with no place.
 */
function alongSide(world: WorldDocument, mapId: string, position: RingPosition): number {
  const place = world.maps.find((m) => m.id === mapId)?.place;
  return position === "north" || position === "south" ? (place?.x ?? 0) : (place?.y ?? 0);
}

/**
 * The placed maps around a map for the overview: those that share a stretch of its edge and those that meet
 * one of its corners, within its own depth (ADR-122 decision 8). Found from the places alone. A gap, a map
 * with no place and a map not in the world give none. Listed clockwise from the north, and along a side
 * from its west or north end.
 *
 * @param world The world.
 * @param mapId The map.
 * @returns The ring, empty when the map is unplaced or absent.
 */
export function overviewRing(world: WorldDocument, mapId: string): RingNeighbour[] {
  const map = world.maps.find((m) => m.id === mapId);
  if (map?.place === undefined) {
    return [];
  }
  const ring: { map: string; position: RingPosition; along: number }[] = [];
  for (const edge of neighboursOf(world, mapId)) {
    ring.push({ map: edge.map, position: edge.side, along: alongSide(world, edge.map, edge.side) });
  }
  for (const other of world.maps) {
    if (other.id === mapId || other.place === undefined || depthOf(other) !== depthOf(map)) {
      continue;
    }
    const corner = cornerOf(map, other);
    if (corner !== null) {
      ring.push({ map: other.id, position: corner, along: 0 });
    }
  }
  ring.sort((a, b) => RING_ORDER.indexOf(a.position) - RING_ORDER.indexOf(b.position) || a.along - b.along);
  return ring.map(({ map: id, position }) => ({ map: id, position }));
}
